import React from 'react'
import { useParams } from 'react-router-dom'
import { Formik } from 'formik'
import { Grid } from '@mui/material'
import ErrorAlert from '../../../components/error/ErrorAlert'
import { StaticItem } from '../../../components/form/DisplayedItem'
import SectionTitle from '../../../components/form/SectionTitle'
import { Section } from '../../../components/layout/Section'
import Loader from '../../../components/loader/Loader'
import BackdropLoader from '../../../components/loader/BackdropLoader'
import FormWithRouteLeavingGuard from '../../../components/form/FormWIthRouteLeavingGuard'
import { EditionModeContextProvider } from '../../../utils/providers/editionModeContext'
import { Status, useProgrammaticCampaignQuery } from '../../../__generated__/graphql'
import { CampaignProgrammaticRoute } from '../../routeParams'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { ProgrammaticCampaignStatusForm } from './ProgrammaticCampaignStatusForm'
import { useProgrammaticCampaignArchiveEligibility } from '../../../components/archiveEntity/useProgrammaticCampaignArchiveEligibility'
import { useArchiveProgrammaticCampaignAction } from '../../../components/archiveEntity/useArchiveProgrammaticCampaignAction'
import { useArchiveStatusOnSubmit } from '../../../components/archiveEntity/useArchiveStatusOnSubmit'
import { useUserPermissions } from '../../company/userPermissions'
import { initialTouchedForAll } from '../../../utils/initialTouchedForAll'

interface ProgrammaticCampaignSummaryFormValues {
  status: Status
}

const ProgrammaticCampaignSummary: React.FC = () => {
  const { campaignProgrammaticId } = useParams<CampaignProgrammaticRoute>()
  const {
    data: programmaticCampaignData,
    loading,
    error,
  } = useProgrammaticCampaignQuery({ variables: { id: campaignProgrammaticId } })
  const t = useAppTranslations()
  const { edit } = useUserPermissions()

  const programmaticCampaign = programmaticCampaignData?.campaignProgrammatic

  const { canArchive, checking: checkingArchiveEligibility } = useProgrammaticCampaignArchiveEligibility(
    programmaticCampaign?.id ?? '',
    programmaticCampaign?.status ?? Status.Active
  )
  const { onArchive, archiving } = useArchiveProgrammaticCampaignAction(programmaticCampaign?.id ?? '')

  const onSubmit = useArchiveStatusOnSubmit<ProgrammaticCampaignSummaryFormValues>({
    initialStatus: programmaticCampaign?.status ?? Status.Inactive,
    canArchive,
    entityName: programmaticCampaign?.name ?? '',
    onArchive,
    onSave: () => undefined,
  })

  if (loading) return <Loader />
  if (error) return <ErrorAlert message={error.message} />
  if (!programmaticCampaign) return null

  const initialValues: ProgrammaticCampaignSummaryFormValues = {
    status: programmaticCampaign.status,
  }

  return (
    <EditionModeContextProvider>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        initialTouched={initialTouchedForAll({ ...initialValues })}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <FormWithRouteLeavingGuard>
            <BackdropLoader open={checkingArchiveEligibility || archiving} />
            <Section sx={{ mb: 0 }}>
              <SectionTitle
                canSubmit={formik.isValid && formik.dirty}
                defaultButtons={canArchive}
                globalSave={canArchive}
                isFirst
                title="programmaticCampaign.sectionTitle"
              />
              <Grid container spacing={1}>
                <Grid size={8}>
                  <StaticItem label={t('commons.name')}>{programmaticCampaign.name}</StaticItem>
                </Grid>
                <Grid size={4}>
                  <ProgrammaticCampaignStatusForm
                    campaign={{ id: programmaticCampaign.id, status: programmaticCampaign.status }}
                    editable={edit}
                  />
                </Grid>
              </Grid>
            </Section>
          </FormWithRouteLeavingGuard>
        )}
      </Formik>
    </EditionModeContextProvider>
  )
}

export default ProgrammaticCampaignSummary
